import { useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import type { ActionPillProps } from './types';

/**
 * The AppBar's trailing capsule: search, and who you are signed in as.
 *
 * **Two actions, where the designs draw five.** The Figma pill also carries a
 * chatbot, notifications and preferences, none of which exist — omitted rather
 * than disabled, so the pill ships narrower than the design.
 *
 * **And the search icon only below tablet**: it is the small-screen stand-in for
 * the AppBar's field, not a companion to it.
 *
 * The profile menu is the only way to end a session, which makes it load-bearing
 * rather than decorative.
 */
export const ActionPill = ({ userLabel, onSearch, onSignOut }: ActionPillProps) => {
  // The node, not a boolean: MUI positions the popover from it, so an `open`
  // flag alone puts the menu in the corner.
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        // A number here is multiplied by theme.shape.borderRadius; the pill
        // radius is absolute.
        borderRadius: `${String(tokens.radius.full)}px`,
        border: 1,
        borderColor: 'border.default',
        backgroundColor: 'surface.raised',
      }}
    >
      {/*
        **Below tablet only.** From tablet up the AppBar carries a real search
        field, and a magnifier beside it would be a second control for the same
        action — which the designs draw and which reads as two different
        searches. Here it is the mobile substitute for a field that does not
        fit, and it goes to the page that has one.

        Omitted entirely rather than shown disabled when `onSearch` is absent:
        search finds only SDLC content, so a session without it has nothing to
        search.
      */}
      {onSearch === undefined ? null : (
        <IconButton
          aria-label="Search the toolkit"
          size="small"
          onClick={onSearch}
          sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      )}

      <IconButton
        size="small"
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}
        // Independent of `userLabel`, which may not have loaded.
        aria-label="Account"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
      >
        <AccountCircleIcon />
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { dense: true } }}
      >
        {/*
          Identity first, then the action. Rendered as a plain Box rather than a
          disabled MenuItem: a disabled item is still in the keyboard order and
          announces as a menu item you cannot use.
        */}
        <Box sx={{ px: 4, py: 2, maxWidth: 280 }}>
          <Typography variant="caption" color="text.secondary" component="p">
            Signed in as
          </Typography>
          <Typography variant="body2" noWrap title={userLabel ?? undefined}>
            {userLabel ?? 'Loading…'}
          </Typography>
        </Box>

        <Divider />

        <MenuItem
          onClick={() => {
            setAnchor(null);
            onSignOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
